import { Center, VStack, Box, Spinner, Flex } from "@reverb-ui/react";

import { AllowFiltering } from "../components";
import ComingSoonContainer from "../components/ComingSoon/ComingSoonContainer";
import { TrafficInsightsReports } from "../presentation/containers/TrafficInsights";
import { TrafficInsightsProvider } from "../presentation/containers/context-api";
import { makeReportsDatasource } from "../main/factories/datasources/reports-datasource-factory";
import { TrafficSourceSummaryReports } from "presentation/containers/TrafficSourceSummary";
import { TrafficSourceSummaryProvider } from "presentation/containers/context-api";
import { DayPartingReports } from "presentation/containers/DayParting";
import { DayPartingProvider } from "presentation/containers/context-api/DayPartingProvider";
import { HighlightsProvider } from "presentation/containers/context-api/HighLightsProvider";
import { Highlights } from "presentation/containers/Highlights";
import {
  ReportsFilterContext,
  ReportsFilterProvider,
} from "../presentation/containers/context-api/ReportsFilterProvider";
import { makeReportsFiltersDatasource } from "../main/factories/datasources/reports-filters-repository";
import { ViewPortRankingProvider } from "../presentation/containers/context-api/ViewPortRankingProvider";
import { ViewPortRankingReports } from "presentation/containers/ViewportRanking";
import { DropoffCalculatorProvider } from "presentation/containers/context-api/DropoffCalculatorProvider";
import { DropoffCalculator } from "presentation/containers/DropoffCalculator";
import { InsightsOnTagsProvider } from "../presentation/containers/context-api/InsightsOnTagsProvider";
import { InsightsOnTags } from "../presentation/containers/InsightsOnTags";
import { SegmentProvider } from "../presentation/containers/context-api/SegmentProvider";
import { PageSummaryReports } from "../presentation/containers/PageSummary";
import { PageSummaryProvider } from "../presentation/containers/context-api/PageSummaryProvider";
import { CreativeSummaryProvider } from "../presentation/containers/context-api/CreativeSummaryProvider";
import { CreativeSummaryReports } from "../presentation/containers/CreativeSummary";
import { CampaignSummaryProvider } from "presentation/containers/context-api/CampaignSummaryProvider";
import { CampaignSummaryReports } from "presentation/containers/CampaignSummary";
import { TopSummariesProvider } from "presentation/containers/context-api/TopSummariesProvider";
import { TopSummariesReports } from "presentation/containers/TopSummaries";
import { PageSpeedProvider } from "presentation/containers/context-api/PageSpeedProvider";
import { PageSpeedReports } from "presentation/containers/PageSpeed";
import { makeReportsSegmentDatasource } from "main/factories/datasources/reports-segment-repository";
import { CreativeGoalProvider } from "presentation/containers/context-api/CreativeGoalProvider";
import { CreativeGoalReports } from "presentation/containers/CreativeGoal";
import { ReportsFilesProvider } from "presentation/containers/context-api/ReportsFilesProvider";
import { makeReportsFilesDatasource } from "main/factories/datasources/reports-files-repository";

export type FilterProps = {
  idPortfolio?: number;
  idCampaign?: number;
  idCreative?: number;
  idUrl?: number;
  startDate?: Date;
  endDate?: Date;
  idRange?: number;
};

export const Reports = () => (
  <SegmentProvider reportsSegmentDatasource={makeReportsSegmentDatasource()}>
    <Center display="flex" flexDirection="column">
      <Box maxW="7xl" w="full">
        <ReportsFilterProvider
          reportsFiltersDatasource={makeReportsFiltersDatasource()}
        >
          <ReportsFilesProvider
            reportsFilesDatasource={makeReportsFilesDatasource()}
          >
            <AllowFiltering />
          </ReportsFilesProvider>
          <ReportsFilterContext.Consumer>
            {({ defaultFiltersLoading, loadingOptions, comparisonParams }) =>
              defaultFiltersLoading ? (
                loadingOptions && (
                  <Flex justifyContent="center">
                    <Spinner size="xl" color="blue.500" />
                  </Flex>
                )
              ) : (
                <VStack spacing={5} w="full">
                  <Flex width="full" gap={5}>
                    <HighlightsProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={0}
                    >
                      <Highlights />
                    </HighlightsProvider>
                    {comparisonParams && (
                      <HighlightsProvider
                        reportsDatasource={makeReportsDatasource()}
                        requestParamsIndex={1}
                      >
                        <Highlights />
                      </HighlightsProvider>
                    )}
                  </Flex>
                  <CreativeGoalProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <CreativeGoalReports />
                  </CreativeGoalProvider>
                  {comparisonParams && (
                    <CreativeGoalProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <CreativeGoalReports />
                    </CreativeGoalProvider>
                  )}
                  <TrafficInsightsProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <TrafficInsightsReports />
                  </TrafficInsightsProvider>
                  {comparisonParams && (
                    <TrafficInsightsProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <TrafficInsightsReports />
                    </TrafficInsightsProvider>
                  )}
                  <InsightsOnTagsProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <InsightsOnTags />
                  </InsightsOnTagsProvider>
                  {comparisonParams && (
                    <InsightsOnTagsProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <InsightsOnTags />
                    </InsightsOnTagsProvider>
                  )}
                  <DropoffCalculatorProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <DropoffCalculator />
                  </DropoffCalculatorProvider>
                  {comparisonParams && (
                    <DropoffCalculatorProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <DropoffCalculator />
                    </DropoffCalculatorProvider>
                  )}
                  <PageSummaryProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <PageSummaryReports />
                  </PageSummaryProvider>
                  {comparisonParams && (
                    <PageSummaryProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <PageSummaryReports />
                    </PageSummaryProvider>
                  )}
                  <TrafficSourceSummaryProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <TrafficSourceSummaryReports />
                  </TrafficSourceSummaryProvider>
                  {comparisonParams && (
                    <TrafficSourceSummaryProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <TrafficSourceSummaryReports />
                    </TrafficSourceSummaryProvider>
                  )}
                  <CreativeSummaryProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <CreativeSummaryReports />
                  </CreativeSummaryProvider>
                  {comparisonParams && (
                    <CreativeSummaryProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <CreativeSummaryReports />
                    </CreativeSummaryProvider>
                  )}
                  <ViewPortRankingProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <ViewPortRankingReports />
                  </ViewPortRankingProvider>
                  {comparisonParams && (
                    <ViewPortRankingProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <ViewPortRankingReports />
                    </ViewPortRankingProvider>
                  )}
                  <CampaignSummaryProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <CampaignSummaryReports />
                  </CampaignSummaryProvider>
                  {comparisonParams && (
                    <CampaignSummaryProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <CampaignSummaryReports />
                    </CampaignSummaryProvider>
                  )}
                  <PageSpeedProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <PageSpeedReports />
                  </PageSpeedProvider>
                  {comparisonParams && (
                    <PageSpeedProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <PageSpeedReports />
                    </PageSpeedProvider>
                  )}
                  <DayPartingProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <DayPartingReports />
                  </DayPartingProvider>
                  {comparisonParams && (
                    <DayPartingProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <DayPartingReports />
                    </DayPartingProvider>
                  )}
                  <TopSummariesProvider
                    reportsDatasource={makeReportsDatasource()}
                    requestParamsIndex={0}
                  >
                    <TopSummariesReports />
                  </TopSummariesProvider>
                  {comparisonParams && (
                    <TopSummariesProvider
                      reportsDatasource={makeReportsDatasource()}
                      requestParamsIndex={1}
                    >
                      <TopSummariesReports />
                    </TopSummariesProvider>
                  )}
                </VStack>
              )
            }
          </ReportsFilterContext.Consumer>
        </ReportsFilterProvider>

        {/*<HStack py={6} spacing={4} px={6}>*/}
        {/*  <Button colorScheme="blue">Schedule this report</Button>*/}
        {/*  <Button colorScheme="green" bg="green.300">Download this report</Button>*/}
        {/*</HStack>*/}

        {/*<ComingSoonContainer />*/}
      </Box>
    </Center>
  </SegmentProvider>
);

export default Reports;
